<template>
    <purchase-contract-section>
        <template #title>
            <div class="flex w-full flex-col">
                <div
                    v-if="linkedCourseNote !== undefined"
                    class="mb-4 flex w-full whitespace-pre-wrap border-4 bg-gray-100 p-6 text-lg font-medium"
                >
                    {{ linkedCourseNote }}
                </div>
                <div class="flex w-full items-center justify-between">
                    <div class="flex flex-col gap-1 md:flex-row">
                        <div>{{ t('purchaseContract.selectCourse') }}</div>
                        <div class="font-normal">
                            <span class="hidden lg:inline">- </span
                            >{{ activeFacility.shortName }}
                        </div>
                    </div>

                    <div>
                        <prime-button
                            :class="[
                                contractData !== undefined && !isActiveSection
                                    ? ''
                                    : 'invisible'
                            ]"
                            @click="emit('editSection')"
                        >
                            <div class="flex items-center gap-2">
                                <i class="pi pi-pencil" />
                                <span class="lg:hidden">{{ t('edit') }}</span>
                                <span class="hidden lg:inline">{{
                                    t('purchaseContract.changeOrder')
                                }}</span>
                            </div>
                        </prime-button>
                    </div>
                </div>
            </div>
        </template>

        <div
            v-if="!isActiveSection && contractData !== undefined"
            class="flex flex-wrap gap-x-16 gap-y-6 py-4"
        >
            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.courseStart')
                }}</label>
                <div>
                    <client-only>
                        {{
                            formatDate(parseLocal(contractData.startDateLocal))
                        }}
                    </client-only>
                </div>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.courseEnd')
                }}</label>
                <div>
                    <client-only>
                        {{ formatDate(parseLocal(contractData.endDateLocal)) }}
                    </client-only>
                </div>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.participants')
                }}</label>
                <div
                    v-if="contractData.enrollmentSpecifications.length === 0"
                    class="text-gray-400"
                >
                    &mdash;
                </div>
                <ul class="list-disc pl-6">
                    <li
                        v-for="enrollmentSpecification in contractData.enrollmentSpecifications"
                        :key="enrollmentSpecification.enrollmentType.id"
                    >
                        {{ enrollmentSpecification.count }}
                        {{ enrollmentSpecification.enrollmentType.singular }}
                    </li>
                </ul>
            </div>

            <div v-if="contractData.promotions.length > 0">
                <label class="font-semibold">{{
                    t('purchaseContract.promotionalCodes')
                }}</label>
                <div class="flex flex-wrap gap-2">
                    <prime-message
                        v-for="promotion in contractData.promotions"
                        :key="promotion.code"
                        severity="success"
                        :closable="false"
                        icon="pi pi-tag"
                        class="!my-0"
                    >
                        <span class="font-semibold">{{ promotion.code }}</span>
                        <template
                            v-if="
                                promotion.promotionCode?.promotion
                                    .translatedDescription ?? ''
                            "
                        >
                            <br />
                            {{
                                promotion.promotionCode?.promotion
                                    .translatedDescription ?? ''
                            }}
                        </template>
                    </prime-message>
                </div>
            </div>
        </div>

        <template v-if="isActiveSection">
            <div class="flex flex-col gap-8 md:flex-row">
                <div class="-mx-4">
                    <div class="mx-4 text-sm">
                        <!-- Keep solid markers on the first row, with patterned markers and Today below. -->
                        <div
                            class="inline-grid grid-cols-[auto_auto_auto] items-center gap-x-4 gap-y-2"
                        >
                            <div class="flex items-center gap-2">
                                <calendar-availability-marker
                                    status="available"
                                />
                                {{ t('purchaseContract.available') }}
                            </div>

                            <div class="flex items-center gap-2">
                                <calendar-availability-marker
                                    status="notOpenYet"
                                />
                                {{ t('purchaseContract.notOpenYet') }}
                            </div>

                            <div class="col-start-1 flex items-center gap-2">
                                <calendar-availability-marker
                                    status="waitlist"
                                />
                                {{ t('purchaseContract.waitlist') }}
                            </div>

                            <div class="flex items-center gap-2">
                                <calendar-availability-marker
                                    status="fullOrClosed"
                                />
                                {{ t('purchaseContract.fullOrClosed') }}
                            </div>

                            <div class="flex items-center gap-2">
                                <div
                                    class="bg-stripes bg-stripes-white relative h-5 w-5 bg-gray-200"
                                ></div>
                                {{ t('purchaseContract.today') }}
                            </div>
                        </div>
                    </div>

                    <prime-calendar
                        v-model="selectedDate"
                        inline
                        select-other-months
                        :pt="{
                            day: ({ context }) => ({
                                'aria-label': getCalendarDayLabel(context.date),
                                class: [
                                    'SelectedCourseDay children:w-[3.2rem] children:h-[3.2rem] children:!rounded-none children:!bg-transparent',
                                    context.today
                                        ? 'bg-gray-200 bg-stripes bg-stripes-white'
                                        : ''
                                ]
                            }),
                            dayLabel: ({ context }) => {
                                // Name the focusable control and expose its selection as button state.
                                return {
                                    role: 'button',
                                    'aria-pressed': context.selected,
                                    'aria-selected': null,
                                    'aria-label': getCalendarDayLabel(
                                        context.date
                                    )
                                }
                            },
                            panel: ({ props }) => ({
                                class: [
                                    'bg-white dark:bg-gray-900',
                                    'min-w-[350px]',
                                    {
                                        'inline-block overflow-x-auto p-2':
                                            props.inline
                                    }
                                ]
                            })
                        }"
                        @month-change="handleMonthChange"
                        @year-change="handleYearChange"
                    >
                        <template #date="{ date }">
                            <select-course-day
                                :dates-to-courses="datesToCourses"
                                :date="date"
                            />
                        </template>
                    </prime-calendar>
                </div>

                <div class="w-full space-y-8">
                    <div
                        class="flex flex-col items-start gap-4 md:flex-row md:gap-12 lg:gap-16"
                    >
                        <div class="space-y-4">
                            <enrollment-type-selectors
                                v-model:enrollment-input="enrollmentInput"
                            />
                        </div>

                        <promotional-code
                            v-model="appliedPromotionalCodes"
                            :plan-id="planId"
                            :start-date="
                                selectedCourse?.boundsStartLocal ?? null
                            "
                        />
                    </div>

                    <div class="flex-1 space-y-4">
                        <div class="font-semibold uppercase tracking-wider">
                            {{ formatTimelessDate(selectedDate) }}
                        </div>

                        <div v-if="coursesForSelectedDate.length === 0">
                            {{
                                t(
                                    'purchaseContract.noCoursesAvailableForDate',
                                    {
                                        date: formatTimelessDate(selectedDate)
                                    }
                                )
                            }}
                        </div>

                        <template
                            v-for="course in coursesForSelectedDate"
                            :key="course.id"
                        >
                            <course-display
                                :course="course"
                                :selected="course.id === selectedCourseId"
                                :availability-threshold="
                                    planData.presentation
                                        ?.availabilityThreshold ?? null
                                "
                                :show-instructors="
                                    planData.presentation
                                        ?.showInstructorNames ?? false
                                "
                                :waitlist-enabled="
                                    planData.bookingLimit.waitlistEnabled
                                "
                                @select="handleEventSelection(course)"
                            />
                        </template>

                        <template v-if="showCourseErrorOrPricing">
                            <app-message
                                v-if="courseIsUnavailableMessage !== null"
                                theme="danger"
                                class="flex justify-center"
                            >
                                {{ courseIsUnavailableMessage }}
                            </app-message>

                            <app-message
                                v-else-if="
                                    courseWaitlistBookingMessage !== null
                                "
                                theme="danger"
                                class="flex justify-center"
                            >
                                <div
                                    class="flex flex-1 flex-col items-center justify-between gap-2 sm:flex-row"
                                >
                                    {{ courseWaitlistBookingMessage.message }}
                                    <prime-button
                                        v-if="
                                            courseWaitlistBookingMessage.type ===
                                            'waitlisted'
                                        "
                                        :label="t('signIn')"
                                        class="shrink-0 whitespace-nowrap"
                                        @click="handleSignIn"
                                    />
                                </div>
                            </app-message>

                            <app-message
                                v-else-if="tooManyEnrollmentsMessage !== null"
                                theme="danger"
                                class="flex justify-center"
                            >
                                {{ tooManyEnrollmentsMessage }}
                            </app-message>

                            <app-message
                                v-else-if="
                                    bookingLimitsErrorMessage !== null &&
                                    continueAttempts > 0
                                "
                                theme="danger"
                                class="flex justify-center"
                            >
                                {{ bookingLimitsErrorMessage }}
                            </app-message>

                            <preview-contract-pricing-table
                                v-else
                                :is-loading="isFetching"
                                :cart="cart"
                                :payment-due-mode="paymentDueMode"
                                :deposit-amount="depositAmount"
                            />
                        </template>
                    </div>
                </div>
            </div>

            <div class="mt-8 flex items-center justify-end gap-4">
                <app-message
                    v-if="waitlistMode === WaitlistMode.Closed"
                    theme="danger"
                >
                    {{
                        t(
                            'purchaseContract.waitlistCloses',
                            {
                                count: planData.bookingLimit
                                    .hoursBeforeCourseWaitlistCloses
                            },
                            planData.bookingLimit
                                .hoursBeforeCourseWaitlistCloses
                        )
                    }}
                </app-message>
                <template
                    v-if="
                        waitlistMode === WaitlistMode.Closed ||
                        waitlistMode === WaitlistMode.Full ||
                        waitlistMode === WaitlistMode.Available
                    "
                >
                    <prime-button
                        :outlined="
                            courseWaitlistBookingMessage !== null &&
                            courseWaitlistBookingMessage.type === 'waitlisted'
                                ? true
                                : undefined
                        "
                        :disabled="waitlistMode !== WaitlistMode.Available"
                        :loading="isFetchingWaitlistData"
                        :label="t('waitlist.join')"
                        @click="handleJoinWaitlist"
                    />
                </template>
                <template v-else>
                    <prime-button
                        v-if="contractData === undefined"
                        :label="t('continue')"
                        :disabled="disableContinueButton"
                        :loading="isMutatingContract"
                        @click="handleContinue"
                    />

                    <prime-button
                        v-else
                        :label="t('continue')"
                        :disabled="disableContinueButton"
                        :loading="isMutatingContract"
                        @click="handleSaveChanges"
                    />
                </template>
            </div>
        </template>
    </purchase-contract-section>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const SelectCourseCourseFragment = graphql(/** GraphQL */ `
    fragment SelectCourseCourseFragment on Course {
        id
        boundsStartLocal
        boundsEndLocal
        bookingStatus
        capacityResult(filter: $capacityResultFilter) {
            enrollments
            contracts
            overlappedBookedSessions
            isUnavailable
            isWaitlistFull
        }
        sessions {
            id
            sessionFacilityHash
        }
        bookingPeriodAvailable(customerId: null, bookingDate: null)
        bookingStatusAvailable(filter: $bookingStatusFilter) {
            customerName
            session {
                sessionId
                startDate
            }
            overrideCapacityConstraints
        }
        ...CourseDisplayCourseFragment
    }
`)

export const SelectCoursePlanFragment = graphql(/** GraphQL */ `
    fragment SelectCoursePlanFragment on Plan {
        id
        terms {
            enrollmentRanges {
                enrollmentType {
                    id
                    singular
                    plural
                }
                minQuantity
                maxQuantity
            }
            allowGuestCheckout
        }
        bookingLimit(input: $input) {
            minimumEnrollments
            maximumEnrollments
            waitlistEnabled
            hoursBeforeCourseWaitlistCloses
        }
        presentation(input: $input, language: $language) {
            availabilityThreshold
            showInstructorNames
            showCustomEvents
        }
    }
`)

export const SelectCourseContractFragment = graphql(/** GraphQL */ `
    fragment SelectCourseContractFragment on PlanContract {
        id
        updatedAtUtc
        draftExpiresAtUtc
        startDateLocal
        endDateLocal
        facility {
            shortName
        }
        enrollmentSpecifications {
            enrollmentType {
                id
                singular
                plural
            }
            count
        }
        course {
            id
            ...SelectCourseCourseFragment
        }
        totalEnrollmentCount
        promotions {
            id
            code
            fullCode
            source
            canRemove
            promotionCode {
                promotion {
                    translatedDescription(language: $language)
                }
            }
        }
    }
`)

export const SelectCoursePlanDataFragment = graphql(/** GraphQL */ `
    fragment SelectCoursePlanDataFragment on Plan {
        courses(filter: $filter, first: $first, after: $after) {
            edges {
                node {
                    ...SelectCourseCourseFragment
                }
            }
            pageInfo {
                hasNextPage
                endCursor
            }
        }
        firstFutureCourse(facilityId: $facilityId) {
            boundsStartLocal
            id
        }
    }
`)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import PrimeButton from 'primevue/button'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import {
    useActiveFacility,
    useRpQuery,
    useShowLoginModal,
    useStorefront,
    useViewer
} from '#imports'
import CourseDisplay from '~/components/PurchaseContract/Program/CourseDisplay.vue'
import {
    formatDate,
    formatTimelessDate,
    now,
    parseLocal,
    parseTimeless,
    toTimelessIso,
    toUtcIsoString
} from '~/composables/support/localization'
import type {
    BookingStatusAvailableFilter,
    CapacityResultFilter,
    ChangeContractInput,
    CreateContractInput,
    SelectCourseCourseFragmentFragment,
    SelectCoursePlanDataFragmentFragment
} from '~/resources/graphql/graphql'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeCalendar, {
    type CalendarDateContext,
    type CalendarMonthChangeEvent,
    type CalendarYearChangeEvent
} from 'primevue/calendar'
import EnrollmentTypeSelectors from '~/components/PurchaseContract/EnrollmentTypeSelectors.vue'
import PreviewContractPricingTable from '~/components/PurchaseContract/PreviewContractPricingTable.vue'
import PrimeMessage from 'primevue/message'
import { usePreviewSessionContractQuery } from '~/components/PurchaseContract/use-preview-session-contract-query'
import {
    endOfMonth,
    endOfWeek,
    max,
    startOfDay,
    startOfMinute,
    startOfWeek as startOfWeekDate
} from 'date-fns'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import SelectCourseDay from '~/components/PurchaseContract/Program/SelectCourseDay.vue'
import CalendarAvailabilityMarker from './CalendarAvailabilityMarker.vue'
import {
    getCourseCalendarAvailability,
    type CourseCalendarInfo
} from './calendar-availability'
import { useRoute } from '#app'
import { navigateTo } from '#app/composables/router'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useLastDeletedContractId } from '~/composables/use-cart'
import PromotionalCode from '~/components/PurchaseContract/PromotionalCode.vue'
import {
    type AppliedPromotionalCode,
    getForInput,
    hasSameAppliedPromotionalCodes
} from '~/components/PurchaseContract/promotional-code'
import { useJoinWaitlistModal } from '~/components/Waitlist/use-join-waitlist-modal'
import AppMessage from '~/components/AppMessage.vue'
import { useTooManyEnrollmentsMessage } from '~/components/PurchaseContract/Program/helpers/use-too-many-enrollments-message'
import { useBookingLimitsErrorMessage } from '~/components/PurchaseContract/Program/helpers/use-booking-limits-error-message'
import { useCourseIsUnavailableMessage } from '~/components/PurchaseContract/Program/helpers/use-course-is-unavailable-message'
import {
    useWaitlistMode,
    WaitlistMode
} from '~/components/PurchaseContract/Program/helpers/use-waitlist-mode'
import { useCourseWaitlistBookingMessage } from '~/components/PurchaseContract/Program/helpers/use-course-waitlist-booking-message'
import { useI18n } from 'vue-i18n'
import { useLocalization } from '~/composables/use-localization'

const props = defineProps<{
    plan: FragmentType<typeof SelectCoursePlanFragment>
    contract: FragmentType<typeof SelectCourseContractFragment> | undefined
    isActiveSection: boolean
    isMutatingContract: boolean
}>()

const emit = defineEmits<{
    (e: 'createContract', payload: CreateContractInput): void
    (e: 'changeContract', payload: ChangeContractInput): void
    (e: 'editSection'): void
}>()

const planData = computed(() =>
    getFragmentData(SelectCoursePlanFragment, props.plan)
)

const contractData = computed(() =>
    props.contract === undefined
        ? props.contract
        : getFragmentData(SelectCourseContractFragment, props.contract)
)

const viewer = useViewer()
const showLoginModal = useShowLoginModal()
const { t } = useI18n({ useScope: 'global' })
const activeFacility = useActiveFacility()
const { startOfWeek: localizedStartOfWeek, dateFormatter } = useLocalization()
const planId = computed(() => planData.value.id)
const facilityId = computed(() => activeFacility.value.id)
const appliedPromotionalCodes = ref<AppliedPromotionalCode[]>([])

const route = useRoute()
const selectedDate = computed<Date>({
    get() {
        const routeDate = route.query.date
        if (
            routeDate === undefined ||
            routeDate === null ||
            routeDate === '' ||
            Array.isArray(routeDate)
        ) {
            return startOfDay(now(activeFacility.value.timezone))
        }

        const parsed = parseTimeless(routeDate)
        if (isNaN(parsed.valueOf())) {
            return startOfDay(now(activeFacility.value.timezone))
        }

        return parsed
    },
    set(newValue) {
        navigateToRoute(
            getSelectedCourseIdFromDate(newValue, selectedDate.value),
            toTimelessIso(newValue)
        )
    }
})

function getSelectedCourseIdFromDate(newDate: Date, oldDate: Date) {
    const newTimeless = toTimelessIso(newDate)
    const oldTimeless = toTimelessIso(oldDate)

    // No change - keep the selected course
    if (oldTimeless === newTimeless) {
        return selectedCourseId.value
    }

    const courseOptionsForDate = datesToCourses.value.get(newTimeless)

    if (courseOptionsForDate === undefined) {
        return undefined
    }

    if (courseOptionsForDate.courses.length === 0) {
        return undefined
    }

    return courseOptionsForDate.courses[0].id
}

// Note: passing in undefined for course will set course to undefined
// Passing undefined for date will not change date
function navigateToRoute(
    courseId: string | undefined,
    date: string | undefined
) {
    navigateTo(
        {
            ...route,
            query: {
                ...route.query,
                course: courseId,
                date: date === undefined ? route.query.date : date
            }
        },
        { replace: true }
    )
}

const viewStartDate = ref()
const viewEndDate = ref()
const calendarWeekStartsOn = computed(() => {
    // date-fns accepts only 0-6 for weekStartsOn; fall back to Sunday if the
    // organization setting ever arrives outside that range.
    if (localizedStartOfWeek.value < 0 || localizedStartOfWeek.value > 6) {
        return 0
    }

    return localizedStartOfWeek.value as 0 | 1 | 2 | 3 | 4 | 5 | 6
})
// month is January => 1, not 0
function calculateViewDates(month: number, year: number) {
    const firstDayOfMonth = parseTimeless(
        year + '-' + String(month).padStart(2, '0') + '-01'
    )
    viewStartDate.value = startOfWeekDate(firstDayOfMonth, {
        weekStartsOn: calendarWeekStartsOn.value
    })
    viewEndDate.value = endOfWeek(endOfMonth(firstDayOfMonth), {
        weekStartsOn: calendarWeekStartsOn.value
    })
}
watch(
    [selectedDate, calendarWeekStartsOn],
    ([selectedViewDate]) => {
        calculateViewDates(
            // Zero based, but calendar event is 1 based
            selectedViewDate.getMonth() + 1,
            selectedViewDate.getFullYear()
        )
    },
    { immediate: true }
)

function handleMonthChange(event: CalendarMonthChangeEvent) {
    // On month change, we set selectedDate to the first of the month
    selectedDate.value = parseTimeless(
        event.year + '-' + String(event.month).padStart(2, '0') + '-01'
    )
    calculateViewDates(event.month, event.year)
}

function handleYearChange(event: CalendarYearChangeEvent) {
    // On month change, we set selectedDate to the first of the month
    selectedDate.value = parseTimeless(
        event.year + '-' + String(event.month).padStart(2, '0') + '-01'
    )
    calculateViewDates(event.month, event.year)
}

// Just get a now when we set this up so we don't have to refetch all the time
const nowAtSetup = startOfMinute(now(activeFacility.value.timezone))

const startDateAfter = computed(() =>
    toUtcIsoString(
        zonedTimeToUtc(
            max([viewStartDate.value, nowAtSetup]),
            activeFacility.value.timezone
        )
    )
)

const startDateBefore = computed(() =>
    toUtcIsoString(
        zonedTimeToUtc(
            max([viewEndDate.value, nowAtSetup]),
            activeFacility.value.timezone
        )
    )
)

const lastDeletedContractId = useLastDeletedContractId()

const capacityResultFilter = computed<CapacityResultFilter>(() => {
    if (contractData.value === undefined) {
        return { ignoreContractId: null }
    }

    // For race condition where deleted contract ID would get used in course calendar query below
    if (lastDeletedContractId.value === contractData.value.id) {
        return { ignoreContractId: null }
    }

    return { ignoreContractId: contractData.value.id }
})

const bookingStatusFilter = computed<BookingStatusAvailableFilter>(() => {
    return {
        customerId: viewer.value !== undefined ? viewer.value.customerId : null
    }
})

const linkedCourseId = computed<string | undefined>(() => {
    const routeLinkedCourseId = route.query.linkedCourse
    if (
        routeLinkedCourseId === undefined ||
        routeLinkedCourseId === null ||
        routeLinkedCourseId === '' ||
        Array.isArray(routeLinkedCourseId)
    ) {
        return undefined
    }

    return routeLinkedCourseId
})

const linkedCourseNote = computed<string | undefined>(() => {
    const routeLinkedCourseNote = route.query.linkedCourseNote
    if (
        routeLinkedCourseNote === undefined ||
        routeLinkedCourseNote === null ||
        routeLinkedCourseNote === '' ||
        Array.isArray(routeLinkedCourseNote)
    ) {
        return undefined
    }

    return routeLinkedCourseNote
})

const courseFilter = computed(() => {
    if (linkedCourseId.value !== undefined) {
        return {
            facilityIds: [facilityId.value],
            linkedCourseId: linkedCourseId.value
        }
    } else {
        return {
            facilityIds: [facilityId.value],
            startDateAfter: startDateAfter.value,
            startDateBefore: startDateBefore.value,
            showCustom: planData.value.presentation?.showCustomEvents ?? false
        }
    }
})

const rpQuery = useRpQuery()
const queryClient = useQueryClient()

const { data } = useQuery({
    queryKey: [
        'CourseCalendarQuery',
        planId,
        courseFilter,
        capacityResultFilter,
        bookingStatusFilter
    ],
    placeholderData: previousData => previousData,
    queryFn: async ({ signal, queryKey }) => {
        const PAGE_SIZE = 200
        const MAX_EVENTS = 1000
        const allEdges = []
        let cursor: string | null = null
        let hasNextPage = true
        let firstFutureCourse = null

        while (allEdges.length < MAX_EVENTS && hasNextPage) {
            // Check abort before making the request
            if (signal.aborted) {
                return
            }

            const result = await rpQuery(
                graphql(`
                    query SelectCoursePlanCoursesQuery(
                        $planId: ID!
                        $filter: CourseFilter!
                        $capacityResultFilter: CapacityResultFilter!
                        $bookingStatusFilter: BookingStatusAvailableFilter!
                        $facilityId: ID!
                        $first: Int!
                        $after: String
                    ) {
                        plan(id: $planId) {
                            ...SelectCoursePlanDataFragment
                        }
                    }
                `),
                {
                    planId: planId.value,
                    filter: courseFilter.value,
                    capacityResultFilter: capacityResultFilter.value,
                    bookingStatusFilter: bookingStatusFilter.value,
                    facilityId: activeFacility.value.id,
                    first: PAGE_SIZE,
                    after: cursor
                }
            )

            if (result.plan === null) {
                throw new Error()
            }

            const planFragmentData = getFragmentData(
                SelectCoursePlanDataFragment,
                result.plan
            )

            allEdges.push(...planFragmentData.courses.edges)
            hasNextPage = planFragmentData.courses.pageInfo.hasNextPage
            cursor = planFragmentData.courses.pageInfo.endCursor
            firstFutureCourse = planFragmentData.firstFutureCourse

            // Check abort after receiving data
            if (signal.aborted) {
                return
            }

            // Update the cache with current progress so UI shows partial data
            const progressiveData = {
                courses: {
                    edges: allEdges.slice(0, MAX_EVENTS),
                    pageInfo: planFragmentData.courses.pageInfo
                },
                firstFutureCourse
            }

            queryClient.setQueryData(queryKey, progressiveData)

            // Stop if we've reached the end or the limit
            if (!hasNextPage || allEdges.length >= MAX_EVENTS) {
                break
            }
        }

        // Return the final data
        return queryClient.getQueryData(queryKey)
    }
})

const datesToCourses = computed(() => {
    // Map from dates to fragments
    const map = new Map<
        string,
        CourseCalendarInfo & {
            courses: SelectCourseCourseFragmentFragment[]
        }
    >()

    data.value?.courses.edges.forEach(edge => {
        const courseData = getFragmentData(
            SelectCourseCourseFragment,
            edge.node
        )

        const key = toTimelessIso(parseLocal(courseData.boundsStartLocal))
        const existing = map.get(key)

        const isCapacityFull = courseData.capacityResult.isUnavailable
        let bookingStatus: 'open' | 'waitlist' | 'closed' = 'open'
        if (courseData.bookingStatus === 'CLOSED') {
            bookingStatus = 'closed'
        } else if (courseData.bookingStatus === 'WAITLIST') {
            if (courseData.capacityResult.isWaitlistFull) {
                bookingStatus = 'closed'
            } else {
                bookingStatus = 'waitlist'
            }
        }

        if (existing !== undefined) {
            existing.capacityFull = existing.capacityFull && isCapacityFull
            existing.bookingPeriodAvailability =
                courseData.bookingPeriodAvailable
            existing.bookingStatus = bookingStatus
            existing.courses.push(courseData)
        } else {
            map.set(key, {
                capacityFull: isCapacityFull,
                bookingPeriodAvailability: courseData.bookingPeriodAvailable,
                bookingStatus: bookingStatus,
                courses: [courseData]
            })
        }
    })

    return map
})

// Label PrimeVue's focusable day as well as its cell; SVG markers alone are
// decorative and cannot communicate availability to screen-reader users.
function getCalendarDayLabel(date: CalendarDateContext): string {
    const calendarDate = new Date(date.year, date.month, date.day)
    const availability = getCourseCalendarAvailability(
        datesToCourses.value.get(toTimelessIso(calendarDate))
    )
    const dateLabel = dateFormatter.value.format(calendarDate)

    return availability === null
        ? dateLabel
        : dateLabel + ', ' + t(`purchaseContract.${availability}`)
}

// Flag variable to make sure we only run the watch once on initial load
const setInitialCourse = ref(true)
// This watch will auto-set our calendar and selected date to the future,
// if the first future course is more than a month away
watch(data, () => {
    if (setInitialCourse.value === false) {
        return
    }

    // If we have data loaded and we are not editing
    if (data.value !== undefined && contractData.value === undefined) {
        // If first future course is null exit
        if (data.value.firstFutureCourse === null) {
            setInitialCourse.value = false
            return
        }

        // If we have a selected course, we don't want to pick a different day
        if (selectedCourseId.value !== undefined) {
            setInitialCourse.value = false
            return
        }

        const firstFutureCourseStartDate = parseLocal(
            data.value.firstFutureCourse.boundsStartLocal
        )
        // If first future course is not in this month
        if (
            firstFutureCourseStartDate.getMonth() !==
            selectedDate.value.getMonth()
        ) {
            // Skip to course date and select course
            navigateToRoute(
                data.value.firstFutureCourse.id,
                toTimelessIso(firstFutureCourseStartDate)
            )
        }
        // Else if there are courses for the current selected date, select a course
        else if (
            datesToCourses.value.get(toTimelessIso(selectedDate.value)) !==
            undefined
        ) {
            navigateToRoute(
                datesToCourses.value.get(toTimelessIso(selectedDate.value))
                    ?.courses[0].id,
                undefined
            )
        }
        setInitialCourse.value = false
    }
})

const enrollmentInput = ref(
    planData.value.terms.enrollmentRanges.map(range => {
        return {
            enrollmentType: range.enrollmentType,
            minQuantity: range.minQuantity,
            maxQuantity: range.maxQuantity,
            count: range.minQuantity
        }
    })
)

const enrollmentTypeCounts = computed(() => {
    return enrollmentInput.value
        .filter(element => element.count > 0)
        .map(element => {
            return {
                enrollmentTypeId: element.enrollmentType.id,
                count: element.count
            }
        })
})

const totalEnrollmentCount = computed(() => {
    return enrollmentInput.value.reduce((prev, curr) => prev + curr.count, 0)
})

const tooManyEnrollmentsMessage = useTooManyEnrollmentsMessage(
    totalEnrollmentCount,
    computed(() => planData.value.bookingLimit.waitlistEnabled),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            bookingStatusAvailable: selectedCourse.value.bookingStatusAvailable,
            enrollments: selectedCourse.value.capacityResult.enrollments,
            isWaitlistFull: selectedCourse.value.capacityResult.isWaitlistFull
        }
    })
)

const courseIsUnavailableMessage = useCourseIsUnavailableMessage(
    computed(() => planData.value.bookingLimit.waitlistEnabled),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            capacityResult: {
                isWaitlistFull:
                    selectedCourse.value.capacityResult.isWaitlistFull,
                overlappedBookedSessions:
                    selectedCourse.value.capacityResult
                        .overlappedBookedSessions,
                contracts: selectedCourse.value.capacityResult.contracts
            }
        }
    })
)

const bookingLimitsErrorMessage = useBookingLimitsErrorMessage(
    totalEnrollmentCount,
    computed(() => planData.value.bookingLimit.minimumEnrollments),
    computed(() => planData.value.bookingLimit.maximumEnrollments)
)

const courseWaitlistBookingMessage = useCourseWaitlistBookingMessage(
    computed(() => planData.value.bookingLimit.waitlistEnabled),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            bookingStatus: selectedCourse.value.bookingStatus,
            bookingStatusAvailable: selectedCourse.value.bookingStatusAvailable,
            capacityResult: {
                isWaitlistFull:
                    selectedCourse.value.capacityResult.isWaitlistFull
            }
        }
    })
)

const showCourseErrorOrPricing = computed(() => {
    if (selectedCourse.value === undefined) {
        return false
    }

    if (selectedCourse.value.bookingPeriodAvailable !== 'AVAILABLE') {
        return false
    }

    if (selectedCourse.value.bookingStatus === 'CLOSED') {
        return false
    }

    if (waitlistMode.value === WaitlistMode.Closed) {
        return false
    }

    return true
})

const coursesForSelectedDate = computed(() => {
    const key = toTimelessIso(selectedDate.value)
    return datesToCourses.value.get(key)?.courses ?? []
})

const selectedCourseId = computed<string | undefined>({
    get() {
        const routeCourseId = route.query.course
        if (
            routeCourseId === undefined ||
            routeCourseId === null ||
            routeCourseId === '' ||
            Array.isArray(routeCourseId)
        ) {
            return undefined
        }

        return routeCourseId
    },
    set(newValue) {
        navigateToRoute(newValue, undefined)
    }
})

const selectedCourse = computed<SelectCourseCourseFragmentFragment | undefined>(
    () => {
        if (selectedCourseId.value === undefined) {
            return undefined
        }

        const courseOptionsForDate = datesToCourses.value.get(
            toTimelessIso(selectedDate.value)
        )

        if (courseOptionsForDate === undefined) {
            return undefined
        }

        return courseOptionsForDate.courses.find(
            course => course.id === selectedCourseId.value
        )
    }
)

function handleEventSelection(event: SelectCourseCourseFragmentFragment) {
    selectedCourseId.value = event.id
}

const selectedSessionId = computed(() => {
    if (selectedCourse.value === undefined) {
        return null
    }

    if (selectedCourse.value.bookingPeriodAvailable !== 'AVAILABLE') {
        return null
    }

    return selectedCourse.value.sessions[0].id
})

const selectedCourseStartDate = computed(() => {
    if (selectedCourse.value === undefined) {
        return null
    }

    if (selectedCourse.value.bookingPeriodAvailable !== 'AVAILABLE') {
        return null
    }

    return selectedCourse.value.boundsStartLocal
})

const {
    cart,
    isFetching,
    paymentDueMode,
    depositAmount,
    promotions,
    subtotal,
    total
} = usePreviewSessionContractQuery(
    selectedSessionId,
    selectedCourseStartDate,
    enrollmentTypeCounts,
    appliedPromotionalCodes
)

const continueAttempts = ref(0)
const canContinue = computed(() => {
    if (selectedCourse.value === undefined) {
        return false
    }

    if (tooManyEnrollmentsMessage.value !== null) {
        return false
    }

    if (bookingLimitsErrorMessage.value !== null) {
        return false
    }

    if (subtotal.value === undefined || total.value === undefined) {
        return false
    }

    return true
})

const storefront = useStorefront()
const allowGuestCheckout = computed<boolean>(
    () =>
        storefront.value.allowGuestCheckout &&
        planData.value.terms.allowGuestCheckout
)

async function handleContinue() {
    if (!canContinue.value) {
        continueAttempts.value += 1
        return
    }

    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: allowGuestCheckout.value
        })
        if (customerId === undefined) {
            return
        }
    }

    emit('createContract', {
        customerId,
        sessionFacilityHash:
            selectedCourse.value.sessions[0].sessionFacilityHash,
        startDate: selectedCourse.value.boundsStartLocal,
        endDate: selectedCourse.value.boundsEndLocal,
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        promotionCodes: getForInput(appliedPromotionalCodes.value),
        subtotal: subtotal.value,
        total: total.value
    })
}

const canSaveChanges = computed(() => {
    if (selectedCourse.value === undefined) {
        return false
    }

    if (tooManyEnrollmentsMessage.value !== null) {
        return false
    }

    if (bookingLimitsErrorMessage.value !== null) {
        return false
    }

    if (subtotal.value === undefined || total.value === undefined) {
        return false
    }

    return true
})

async function handleSaveChanges() {
    if (!canSaveChanges.value) {
        continueAttempts.value += 1
        return
    }

    if (contractData.value === undefined) {
        // We should not call save changes once we have an ID
        throw new Error()
    }

    emit('changeContract', {
        id: contractData.value.id,
        updatedAt: contractData.value.updatedAtUtc,
        startSessionFacilityHash:
            selectedCourse.value.sessions[0].sessionFacilityHash,
        startDate: selectedCourse.value.boundsStartLocal,
        endDate: selectedCourse.value.boundsEndLocal,
        promotionCodes: getForInput(appliedPromotionalCodes.value),
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        subtotal: subtotal.value,
        total: total.value
    })
}

watch(
    promotions,
    newValue => {
        if (newValue === undefined || newValue === null) {
            return
        }

        const nextPromotions = newValue.map(promotion => {
            return {
                code: promotion.code,
                fullCode: promotion.fullCode,
                source: promotion.source,
                canRemove: promotion.canRemove,
                description:
                    promotion.promotionCode.promotion.translatedDescription ??
                    ''
            }
        })

        if (
            !hasSameAppliedPromotionalCodes(
                appliedPromotionalCodes.value,
                nextPromotions
            )
        ) {
            // The preview result is the source of truth for the effective
            // promotion set used for pricing and the eventual mutation payload.
            appliedPromotionalCodes.value = nextPromotions
        }
    },
    { immediate: true }
)

watch(
    contractData,
    newValue => {
        if (newValue === undefined) {
            return
        }

        enrollmentInput.value = enrollmentInput.value.map(input => {
            const contractInput = newValue.enrollmentSpecifications.find(
                spec => spec.enrollmentType.id === input.enrollmentType.id
            )

            if (contractInput === undefined) {
                return { ...input }
            }

            return {
                ...input,
                count: contractInput.count
            }
        })

        // A null course is an orphaned booking, so they just need to pick a new
        // course
        if (newValue.course !== null) {
            const courseData = getFragmentData(
                SelectCourseCourseFragment,
                newValue.course
            )
            const newDate = toTimelessIso(
                parseLocal(courseData.boundsStartLocal)
            )
            const newCourseId = courseData.id
            // If we are not on the correct date/course redirect
            if (
                toTimelessIso(selectedDate.value) !== newDate ||
                newCourseId !== selectedCourseId.value
            ) {
                navigateToRoute(newCourseId, newDate)
            }
        }

        appliedPromotionalCodes.value = newValue.promotions.map(promotion => ({
            contractPromotionId: promotion.id,
            code: promotion.code,
            fullCode: promotion.fullCode,
            source: promotion.source,
            canRemove: promotion.canRemove,
            description:
                promotion.promotionCode?.promotion.translatedDescription ?? ''
        }))
    },
    { immediate: true }
)

const disableContinueButton = computed(() => {
    return (
        !showCourseErrorOrPricing.value ||
        courseIsUnavailableMessage.value !== null ||
        courseWaitlistBookingMessage.value !== null
    )
})

const waitlistMode = useWaitlistMode(
    computed(() => {
        return {
            waitlistEnabled: planData.value.bookingLimit.waitlistEnabled,
            hoursBeforeCourseWaitlistCloses:
                planData.value.bookingLimit.hoursBeforeCourseWaitlistCloses
        }
    }),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            boundsStartLocal: selectedCourse.value.boundsStartLocal,
            bookingStatus: selectedCourse.value.bookingStatus,
            bookingStatusAvailable: selectedCourse.value.bookingStatusAvailable,
            capacityResult: {
                isWaitlistFull:
                    selectedCourse.value.capacityResult.isWaitlistFull,
                overlappedBookedSessions:
                    selectedCourse.value.capacityResult
                        .overlappedBookedSessions,
                contracts: selectedCourse.value.capacityResult.contracts,
                enrollments: selectedCourse.value.capacityResult.enrollments
            }
        }
    })
)

const { isFetching: isFetchingWaitlistData, showJoinWaitlistModal } =
    useJoinWaitlistModal()
async function handleJoinWaitlist() {
    if (selectedCourse.value === undefined) {
        throw new Error()
    }

    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: false
        })
        if (customerId === undefined) {
            return
        }
    }

    await showJoinWaitlistModal({
        courseId: selectedCourse.value.id,
        facilityId: activeFacility.value.id
    })
}

async function handleSignIn() {
    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: false
        })
        if (customerId === undefined) {
            return
        }
    }

    // Booking status filter should force a refresh of the query
}
</script>

<style lang="postcss">
.SelectedCourseDay > span[data-p-highlight='true'] {
    /* Keep selection neutral so the brand color belongs to availability markers. */
    @apply border border-gray-400;
}
</style>
